<template>
  <div class="jim-scheduling-container profit-analysis-page">

    <!-- Header row -->
    <div class="page-header-row">
      <div></div>
      <h1 class="page-heading">COST PROFIT ANALYSIS</h1>
      <div></div>
    </div>

    <div class="main-content">

      <!-- ✅ LEFT SIDE CONTROLS -->
      <div class="left-side-section">

        <div class="control">
          <label>Product Type</label>
          <select v-model="selectedProductType" @change="loadItems">
            <option value="">Select...</option>
            <option v-for="t in productTypes" :key="t.product_type" :value="t.product_type">
              {{ t.product_type }}
            </option>
          </select>
        </div>

        <div class="control">
          <label>Freight Out %</label>
          <input v-model.number="freightPct" type="number" step="0.01" />
        </div>

        <div class="control">
          <label>Sales Comm %</label>
          <input v-model.number="commPct" type="number" step="0.01" />
        </div>

        <div class="control">
          <label>Rebate %</label>
          <input v-model.number="rebatePct" type="number" step="0.01" />
        </div>

        <div class="control">
          <label>Desired GP %</label>
          <input v-model.number="desiredGpPct" type="number" step="0.01" />
        </div>

        <div class="control">
          <label>Target Contribution Margin %</label>
          <input v-model.number="targetCmPct" type="number" step="0.01" />
          <div class="small-muted" style="text-align:center; margin-top:-4px;">
            Used for row highlighting and suggested pricing
          </div>
        </div>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="fillPricesFromGP">
          Fill Prices from GP%
        </button>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="suggestPricesFromTarget">
          Fill Prices from CM%
        </button>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="togglePasteBox">
          {{ showPasteBox ? "Hide" : "Quick Paste Prices" }}
        </button>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="exportCSV">
          Export CSV
        </button>
        
        <!-- ✅ Quick Paste Box -->
        <div v-if="showPasteBox" class="panel" style="width:100%;">
          <div style="font-weight:bold; margin-bottom:6px;">Paste Item + Price</div>
          <div style="font-size:12px; opacity:0.9; margin-bottom:8px;">
            Example: MHP25R6B-05 21.90
          </div>
          <textarea
            v-model="pasteText"
            rows="7"
            style="width:100%; border-radius:8px; padding:8px; background:#111; color:#cbbfbf; border:1px solid #444; font-family:inherit;"
          ></textarea>

          <button class="plan-it-btn" style="margin-top:10px;" @click="applyPastedPrices">
            Apply Pasted Prices
          </button>
        </div>

        <!-- ✅ Error -->
        <div v-if="error" class="panel error-box" style="margin-top:10px;">
          {{ error }}
        </div>

      </div>

      <!-- ✅ RIGHT SIDE CONTENT -->
      <div class="right-side-section">

        <!-- Summary -->
        <div v-if="rowsLoaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">Rows</div>
            <div class="summary-value">{{ rows.length }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Avg GP%</div>
            <div class="summary-value">{{ formatPct(summary.avgGpPct) }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Avg CM%</div>
            <div class="summary-value">{{ formatPct(summary.avgCmPct) }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Lowest Margin</div>
            <div class="summary-value">{{ summary.worstItem }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Highest Margin</div>
            <div class="summary-value">{{ summary.bestItem }}</div>
          </div>
        </div>

        <!-- Scrollable Table -->
        <div v-if="rowsLoaded" class="sheet-scroll-container">
          <div class="sheet-wrapper">
            <div class="sheet-page panel">

              <div class="sheet-page-header">
                <h2>{{ selectedProductType }} — {{ selectedLocation }}</h2>
                <div class="small-muted">
                  Freight {{ freightPct }}% • Comm {{ commPct }}% • Rebate {{ rebatePct }}%
                </div>
              </div>

              <div class="table-scroll">
                <table class="price-table">
                  <thead>
                    <tr>
                      <th class="sticky-col">Item No</th>
                      <th class="desc-col">Description</th>
                      <th>Std Cost</th>

                      <th class="price-input-col">Sell Price</th>
                      <th>GP%</th>
                      <th>CM%</th>

                      <th>Freight $</th>
                      <th>Comm $</th>
                      <th>Rebate $</th>
                      <th>Total Burden $</th>
                      <th>CM $</th>

                      <th>Suggested (GP%)</th>
                      <th>Suggested (Target CM%)</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="r in rows"
                      :key="r.item_no"
                      :class="rowClass(r)"
                    >
                      <td class="sticky-col">{{ r.item_no }}</td>
                      <td class="desc-col">{{ r.description }}</td>
                      <td>{{ formatMoney(r.standard_cost) }}</td>

                      <!-- ✅ Sell Price Input -->
                      <td class="price-input-col">
                        <input
                          v-model.number="r.sell_price"
                          type="number"
                          step="0.01"
                          class="price-input"
                        />
                      </td>

                      <td>{{ formatPct(calcRow(r).gp_pct) }}</td>
                      <td>{{ formatPct(calcRow(r).cm_pct) }}</td>

                      <td>{{ formatMoney(calcRow(r).freight_dol) }}</td>
                      <td>{{ formatMoney(calcRow(r).comm_dol) }}</td>
                      <td>{{ formatMoney(calcRow(r).rebate_dol) }}</td>
                      <td>{{ formatMoney(calcRow(r).total_burden) }}</td>
                      <td>{{ formatMoney(calcRow(r).cm_dol) }}</td>

                      <td>{{ formatMoney(calcRow(r).suggested_from_gp) }}</td>
                      <td>{{ formatMoney(calcRow(r).suggested_from_target) }}</td>
                    </tr>

                    <tr v-if="rowsLoaded && (!rows || rows.length === 0)">
                      <td colspan="13" class="empty-row">
                        No item rows returned for this Product Type.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="print-footer" style="margin-top:12px;">
                <div class="footer-text">
                  Contribution Margin % = profit remaining after Standard Cost + Freight Out + Sales Commission + Rebate.
                </div>
              </div>

            </div>
          </div>
        </div>

        <div v-else class="panel notes-panel">
          <div class="notes-title">Instructions</div>
          <div class="notes-body">
            Select a Product Type to load all items for your location. Enter Freight/Comm/Rebate assumptions.
            Type ad hoc Sell Prices or use the bulk pricing buttons.
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ProfitAnalysis",

  data() {
    return {
      productTypes: [],
      selectedProductType: "",
      rows: [],
      freightPct: 0.0,
      commPct: 0.0,
      rebatePct: 0.0,
      desiredGpPct: 38.0,
      targetCmPct: 25.0,

      error: null,
      rowsLoaded: false,

      showPasteBox: false,
      pasteText: "",

      summary: {
        avgGpPct: 0,
        avgCmPct: 0,
        worstItem: "",
        bestItem: ""
      }
    };
  },

  computed: {
    // ✅ matches JIMScheduling: pull location from root (App.vue)
    selectedLocation() {
      return this.$root.selectedLocation || "D";
    }
  },

  watch: {
    // ✅ keep summary reactive to edits and assumptions
    rows: {
      deep: true,
      handler() {
        this.computeSummary();
      }
    },
    freightPct() { this.computeSummary(); },
    commPct() { this.computeSummary(); },
    rebatePct() { this.computeSummary(); },
    desiredGpPct() { this.computeSummary(); },
    targetCmPct() { this.computeSummary(); },

    // ✅ if location changes, clear current selections
    selectedLocation(newLoc, oldLoc) {
      if (newLoc !== oldLoc) {
        this.selectedProductType = "";
        this.rowsLoaded = false;
        this.rows = [];
        this.loadProductTypes();
      }
    }
  },

  async mounted() {
    await this.loadProductTypes();
  },

  methods: {
    async loadProductTypes() {
      this.error = null;
      try {
        const { data } = await axios.get("/getProfitAnalysisTypes", {
          params: { location: this.selectedLocation }
        });
        this.productTypes = data.product_types || [];
      } catch (err) {
        console.error("loadProductTypes error:", err);
        this.error = err?.response?.data?.error || "Failed to load product types.";
      }
    },

    async loadItems() {
      this.error = null;
      this.rowsLoaded = false;
      this.rows = [];

      if (!this.selectedProductType) return;

      try {
        const { data } = await axios.get("/getProfitAnalysisItems", {
          params: {
            location: this.selectedLocation,
            product_type: this.selectedProductType
          }
        });

        this.rows = (data.rows || []).map(r => ({
          ...r,
          sell_price: null
        }));

        this.rowsLoaded = true;
        this.computeSummary();
      } catch (err) {
        console.error("loadItems error:", err);
        this.error = err?.response?.data?.error || "Failed to load item rows.";
      }
    },

    // ✅ calculate all row values from current inputs
    calcRow(r) {
      const freight = (this.freightPct || 0) / 100;
      const comm = (this.commPct || 0) / 100;
      const rebate = (this.rebatePct || 0) / 100;

      const desiredGp = (this.desiredGpPct || 0) / 100;
      const targetCM = (this.targetCmPct || 0) / 100;

      const cost = Number(r.standard_cost || 0);

      // Suggested from desired GP%
      const denomGp = 1 - desiredGp;
      const suggestedFromGp = denomGp > 0 ? cost / denomGp : null;

      // Suggested from target CM%
      const denomTarget = 1 - (freight + comm + rebate + targetCM);
      const suggestedFromTarget = denomTarget > 0 ? cost / denomTarget : null;

      const sell =
        r.sell_price != null && r.sell_price !== ""
          ? Number(r.sell_price)
          : null;

      if (!sell || sell <= 0) {
        return {
          gp_pct: null,
          cm_pct: null,
          freight_dol: null,
          comm_dol: null,
          rebate_dol: null,
          total_burden: null,
          cm_dol: null,
          suggested_from_gp: suggestedFromGp,
          suggested_from_target: suggestedFromTarget
        };
      }

      const gpPct = (sell - cost) / sell;

      const freightDol = sell * freight;
      const commDol = sell * comm;
      const rebateDol = sell * rebate;
      const totalBurden = freightDol + commDol + rebateDol;

      const cmDol = sell - cost - totalBurden;
      const cmPct = cmDol / sell;

      return {
        gp_pct: gpPct,
        cm_pct: cmPct,
        freight_dol: freightDol,
        comm_dol: commDol,
        rebate_dol: rebateDol,
        total_burden: totalBurden,
        cm_dol: cmDol,
        suggested_from_gp: suggestedFromGp,
        suggested_from_target: suggestedFromTarget
      };
    },

    fillPricesFromGP() {
      const gp = (this.desiredGpPct || 0) / 100;
      const denom = 1 - gp;

      if (denom <= 0) {
        this.error = "Desired GP% must be less than 100%.";
        return;
      }

      this.error = null;

      this.rows = this.rows.map(r => {
        const cost = Number(r.standard_cost || 0);
        const sell = cost ? cost / denom : null;
        return { ...r, sell_price: sell ? Number(sell.toFixed(2)) : null };
      });
    },

    suggestPricesFromTarget() {
      const freight = (this.freightPct || 0) / 100;
      const comm = (this.commPct || 0) / 100;
      const rebate = (this.rebatePct || 0) / 100;
      const target = (this.targetCmPct || 0) / 100;

      const denom = 1 - (freight + comm + rebate + target);

      if (denom <= 0) {
        this.error =
          "Freight + Comm + Rebate + Target CM% is >= 100%. Reduce % assumptions.";
        return;
      }

      this.error = null;

      this.rows = this.rows.map(r => {
        const cost = Number(r.standard_cost || 0);
        const sell = cost ? cost / denom : null;
        return { ...r, sell_price: sell ? Number(sell.toFixed(2)) : null };
      });
    },

    togglePasteBox() {
      this.showPasteBox = !this.showPasteBox;
    },

    applyPastedPrices() {
      this.error = null;
      if (!this.pasteText.trim()) return;

      const map = {};
      this.pasteText
        .split("\n")
        .map(x => x.trim())
        .filter(Boolean)
        .forEach(line => {
          const parts = line.split(/\s+/);
          if (parts.length >= 2) {
            const item = parts[0].trim();
            const price = Number(parts[1]);
            if (item && !Number.isNaN(price)) map[item] = price;
          }
        });

      this.rows = this.rows.map(r => {
        const p = map[r.item_no];
        return p != null ? { ...r, sell_price: Number(p.toFixed(2)) } : r;
      });
    },

    computeSummary() {
      const valid = (this.rows || [])
        .map(r => ({ item_no: r.item_no, ...this.calcRow(r) }))
        .filter(x => x.gp_pct != null && x.cm_pct != null);

      if (!valid.length) {
        this.summary = { avgGpPct: 0, avgCmPct: 0, worstItem: "", bestItem: "" };
        return;
      }

      const avgGp = valid.reduce((a, r) => a + r.gp_pct, 0) / valid.length;
      const avgCM = valid.reduce((a, r) => a + r.cm_pct, 0) / valid.length;

      const sorted = [...valid].sort((a, b) => a.cm_pct - b.cm_pct);

      this.summary.avgGpPct = avgGp;
      this.summary.avgCmPct = avgCM;
      this.summary.worstItem = sorted[0]?.item_no || "";
      this.summary.bestItem = sorted[sorted.length - 1]?.item_no || "";
    },

    rowClass(r) {
      const target = (this.targetCmPct || 0) / 100;
      const calc = this.calcRow(r);

      if (r.sell_price != null && r.sell_price !== "" && r.standard_cost != null) {
        if (Number(r.sell_price) <= Number(r.standard_cost)) return "dangerRow";
      }

      if (calc.cm_pct != null && calc.cm_pct < target) return "belowTarget";
      return "";
    },

    exportCSV() {
      const headers = [
        "Item No", "Description", "Std Cost", "Sell Price",
        "GP%", "CM%", "Freight $", "Comm $", "Rebate $",
        "Total Burden $", "CM $", "Suggested (GP%)", "Suggested (Target CM%)"
      ];

      const rows = this.rows.map(r => {
        const c = this.calcRow(r);
        return [
          r.item_no,
          (r.description || "").replace(/,/g, " "),
          r.standard_cost ?? "",
          r.sell_price ?? "",
          c.gp_pct ?? "",
          c.cm_pct ?? "",
          c.freight_dol ?? "",
          c.comm_dol ?? "",
          c.rebate_dol ?? "",
          c.total_burden ?? "",
          c.cm_dol ?? "",
          c.suggested_from_gp ?? "",
          c.suggested_from_target ?? ""
        ];
      });

      const csv = [headers, ...rows]
        .map(row => row.map(v => `"${v}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `ProfitAnalysis_${this.selectedProductType}_${this.selectedLocation}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },

    formatMoney(v) {
      if (v == null || v === "") return "";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(v);
    },

    formatPct(v) {
      if (v == null || v === "") return "";
      return (v * 100).toFixed(2) + "%";
    }
  }
};
</script>



<style scoped>

/* ✅ PAGE BASE — Pablo black */
.price-sheet-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* ✅ BASE CONTAINER — matches JIMScheduling */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* ✅ Header row like JIMScheduling */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* ✅ MAIN LAYOUT — left controls, right scrollable content */
.main-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* ✅ Left-side controls (classic Pablo) */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 120px;
}

/* ✅ Right-side workspace */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* ✅ Scrollable sheet area like MachinesView */
.sheet-scroll-container {
  max-height: 80vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

/* scrollbar subtle */
.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}
.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}
.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

/* ✅ Pablo dark panel */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* ✅ Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* ✅ Pablo buttons (plan-it-btn style) */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

/* ✅ Error box style (danger) */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

/* ✅ Summary bar (Pablo slate blue) */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

/* summary metrics */
.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
}

/* ✅ Notes panel */
.notes-panel {
  margin-bottom: 12px;
}

.notes-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 6px;
}

.notes-body {
  font-size: 14px;
  line-height: 1.35;
  white-space: pre-wrap;
}

/* ✅ Page wrapper */
.sheet-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ✅ Each page has Pablo blue-ish panel feel */
.sheet-page {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

/* ✅ Page header above each table */
.sheet-page-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.sheet-page-header h2 {
  margin: 0;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  color: #fff;
}

.small-muted {
  font-size: 12px;
  opacity: 0.8;
  color: #fff;
}

/* ✅ Table scroll horizontal */
.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

/* ✅ Pricing matrix table */
.price-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

/* base cells */
.price-table th,
.price-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  text-align: center;
}

/* header row (dark) */
.price-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

/* sticky left column */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  min-width: 120px;
  text-align: left !important;
  color: #cbbfbf;
}

/* row hover = MachinesView vibe */
.price-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

/* columns */
.group-cell {
  font-weight: bold;
}

.desc-col {
  min-width: 220px;
  text-align: left !important;
}

.pkg-col {
  min-width: 60px;
}

.diam-col,
.price-cell {
  min-width: 55px;
}

.dash {
  opacity: 0.6;
}

.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.75;
  text-align: center;
}

/* ✅ Freight multiplier row */
.freight-row td {
  background: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
}

.freight-cell {
  text-align: center !important;
  padding: 10px 12px !important;
}

.freight-line {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 10px;
  text-align: center;
}

.freight-label {
  opacity: 0.85;
  font-weight: 600;
}

.freight-value {
  font-weight: bold;
  color: #fff;
}

.sep {
  opacity: 0.5;
  margin: 0 4px;
}

/* ✅ Footer */
.print-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #333;
  color: #fff;
  font-size: 12px;
  line-height: 1.35;
}

.footer-text {
  white-space: pre-wrap;
}

.footer-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.footer-line {
  font-style: italic;
  opacity: 0.9;
  margin-bottom: 6px;
}

.footer-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 10px;
}

.footer-row .k {
  font-weight: bold;
  white-space: nowrap;
}

.footer-row .v {
  opacity: 0.95;
}

/* ✅ PRINT STYLING */
@media print {

  /* Hide controls */
  .left-side-section,
  .error-box {
    display: none !important;
  }

  /* Full width print */
  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  /* Each sheet page = printed page */
  .sheet-page {
    page-break-after: always;
    break-after: page;
    display: flex !important;
    flex-direction: column !important;
    min-height: 95vh !important;
    background: white !important;
    border: none !important;
  }

  /* table expands */
  .table-scroll {
    flex: 1 1 auto !important;
    overflow: visible !important;
  }

  /* footer bottom */
  .print-footer {
    margin-top: auto !important;
    border-top: 1px solid #999 !important;
    color: black !important;
  }

  .print-footer * {
    color: black !important;
  }

  /* Convert all panels white */
  .jim-scheduling-container,
  .panel,
  .summary-bar {
    background: white !important;
    color: black !important;
    border: none !important;
  }

  /* header table */
  .price-table th {
    background: #eee !important;
    color: black !important;
  }

  .sticky-col {
    background: white !important;
    color: black !important;
  }

  /* Freight row print readability */
  .freight-row td {
    background: #f3f3f3 !important;
    color: black !important;
  }

  .freight-value {
    color: black !important;
  }
}


.profit-analysis-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

select {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.price-input-col {
  min-width: 120px;
}

.price-input {
  width: 80%;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  text-align: center;
}

.belowTarget td {
  background-color: #7a2323 !important;
  color: #fff !important;
}

.dangerRow td {
  background-color: #3b0d0d !important;
  color: #ffb3b3 !important;
  font-weight: bold;
}
</style>
